// ═══════════════════════════════════════════════════════════════════════════
// OPTIMIZED USER ACCOUNT CREATION DIALOG - NO TYPING DELAY
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, memo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { CreateDialog, FieldLabel } from "./shared";

const USER_ROLE_OPTIONS = [
  { value: "operator", label: "Operator" },
  { value: "supervisor", label: "Supervisor" },
  { value: "machinesupervisor", label: "Machine Supervisor" },
  { value: "planner", label: "Planner" },
  { value: "hr", label: "HR" },
  { value: "admin", label: "Admin" },
];

const UserCreateDialog = memo(function UserCreateDialog({
  open,
  onClose,
  onSave,
  saving,
}) {
  // ✅ Local state - only this dialog re-renders on typing
  const [form, setForm] = useState({
    Employee_ID: "",
    Password: "",
    User_Category: "operator",
  });

  const handleClose = () => {
    // Reset form when closing
    setForm({
      Employee_ID: "",
      Password: "",
      User_Category: "operator",
    });
    onClose();
  };

  const handleSave = () => {
    onSave(form);
    // Reset form after save
    setForm({
      Employee_ID: "",
      Password: "",
      User_Category: "operator",
    });
  };

  return (
    <CreateDialog
      open={open}
      onClose={handleClose}
      title="Create User Account"
      icon={<ManageAccountsIcon />}
      color="#7c3aed"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Employee ID *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.Employee_ID}
            onChange={(e) =>
              setForm((f) => ({ ...f, Employee_ID: e.target.value }))
            }
            autoFocus
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Password *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="password"
            value={form.Password}
            onChange={(e) =>
              setForm((f) => ({ ...f, Password: e.target.value }))
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Role *</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={form.User_Category}
              onChange={(e) =>
                setForm((f) => ({ ...f, User_Category: e.target.value }))
              }
            >
              {USER_ROLE_OPTIONS.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: "none" }}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={15} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? "Creating..." : "Create User Account"}
        </Button>
      </Box>
    </CreateDialog>
  );
});

export default UserCreateDialog;
